import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  EmojiEvents as RewardIcon,
  Stars as StarsIcon,
  Info as InfoIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Redeem as RedeemIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { rewardService } from '../../services/reward';
import { useAuth } from '../../context/AuthContext';

const AssociateReward = () => {
  const history = useHistory();
  const { user } = useAuth();

  const [rewards, setRewards] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReward, setSelectedReward] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redeemingReward, setRedeemingReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const calculateDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    end.setHours(0, 0, 0, 0);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const isRewardExpired = (endDate) => {
    if (!endDate) return false;
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return end < today;
  };

  const isRedeemDisabled = (reward) => {
    // If quantity is null and endDate is null, allow redeem
    if (reward.quantity === null && !reward.endDate) {
      return false;
    }
    // Disable if expired
    if (isRewardExpired(reward.endDate)) {
      return true;
    }
    // Disable if quantity is 0 and hasQuantity is true
    if (reward.hasQuantity && reward.quantity === 0) {
      return true;
    }
    return false;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (error) {
      return 'N/A';
    }
  };

  const mapApiRewardToLocal = (apiReward) => {
    // Format rewardId: if it's a number, format as RWD-{number}, otherwise use as-is
    const formattedRewardId = apiReward.rewardId
      ? (typeof apiReward.rewardId === 'number' ? `${apiReward.rewardId}` : apiReward.rewardId)
      : 'N/A';

    const endDate = apiReward.endDate || null;
    const quantity = apiReward.quantity;
    const hasQuantity = quantity !== null && quantity !== undefined;
    const isExpired = endDate ? isRewardExpired(endDate) : false;

    return {
      id: apiReward._id || apiReward.id,
      rewardId: formattedRewardId,
      name: apiReward.title || '',
      pointsRequired: apiReward.redeemPoints || 0,
      description: apiReward.description || '',
      status: apiReward.status === 'active' ? 'Active' : 'Inactive',
      hasQuantity: hasQuantity,
      quantity: hasQuantity ? quantity : null,
      hasExpiry: !!endDate,
      expiryDays: endDate ? (apiReward.daysRemaining || calculateDaysRemaining(endDate)) : 0,
      isExpired: isExpired,
      startDate: apiReward.startDate || '',
      endDate: endDate,
    };
  };

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const data = await rewardService.getActiveRewards();
      const mappedRewards = Array.isArray(data) ? data.map(mapApiRewardToLocal) : [];
      setRewards(mappedRewards);
    } catch (err) {
      setErrorDialogOpen(true);
      setDialogMessage(err?.message || 'Failed to fetch active rewards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewards();
  }, []);


  const handleRedeem = (reward) => {
    setRedeemingReward(reward);
    setRedeemDialogOpen(true);
  };

  const handleCloseRedeemDialog = () => {
    setRedeemDialogOpen(false);
    setRedeemingReward(null);
  };

  const handleConfirmRedeem = async () => {
    if (!redeemingReward) return;

    try {
      setRedeeming(true);
      // Dynamic associateId from user context
      const associateId = user?.userId;

      if (!associateId) {
        throw new Error('User not authenticated or ID missing');
      }

      // Use reward.id (which is the _id from API) as rewardId
      const response = await rewardService.redeemReward(redeemingReward.id, associateId);

      if (response.status === true || response.success === true) {
        setRedeemDialogOpen(false);
        setRedeemingReward(null);
        setSuccessDialogOpen(true);
        setDialogMessage(response.message || `Reward "${redeemingReward.name}" redeemed successfully!`);
        // Refresh the rewards list to update quantities
        fetchRewards();
      } else {
        throw new Error(response.message || 'Failed to redeem reward');
      }
    } catch (err) {
      setErrorDialogOpen(true);
      setDialogMessage(err?.message || 'Failed to redeem reward');
    } finally {
      setRedeeming(false);
    }
  };

  const handleView = (reward) => {
    setSelectedReward(reward);
    setViewDialogOpen(true);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Inactive':
        return { backgroundColor: '#ffebee', color: '#c62828' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };


  const filteredRewards = rewards.filter((reward) => {
    const matchesSearch =
      searchQuery === '' ||
      reward.rewardId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reward.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reward.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || reward.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RewardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Associate Reward Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchRewards}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Search and Filter Section */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search by Reward ID, Name or Description"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 4 }}>
            <CircularProgress size={50} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Reward ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Points Required</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Expiry Days</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRewards.length > 0 ? (
                  filteredRewards
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((reward, index) => (
                      <TableRow key={reward.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium" color="primary">
                            {reward.rewardId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {reward.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={reward.description}
                          >
                            {reward.description || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarsIcon sx={{ fontSize: 16, color: '#f57c00' }} />
                            <Typography variant="body2" fontWeight="medium">
                              {reward.pointsRequired.toLocaleString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(reward.startDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {reward.endDate ? formatDate(reward.endDate) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {reward.hasQuantity ? (
                            reward.quantity === 0 ? (
                              <Chip
                                label="Out of Stock"
                                size="small"
                                sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 'medium' }}
                              />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <InventoryIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                <Typography variant="body2" fontWeight="medium">
                                  {reward.quantity}
                                </Typography>
                              </Box>
                            )
                          ) : (
                            <Chip label="N/A" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#666' }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {reward.hasExpiry ? (
                            reward.isExpired ? (
                              <Chip
                                label="Expired"
                                size="small"
                                sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 'medium' }}
                              />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <StarsIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                                <Typography variant="body2" fontWeight="medium">
                                  {reward.expiryDays} days
                                </Typography>
                              </Box>
                            )
                          ) : (
                            <Chip label="N/A" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#666' }} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="View Details">
                              <IconButton color="info" size="small" onClick={() => handleView(reward)}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                isRedeemDisabled(reward)
                                  ? (reward.isExpired
                                    ? 'Reward has expired'
                                    : reward.hasQuantity && reward.quantity === 0
                                      ? 'Reward is out of stock'
                                      : 'Cannot redeem this reward')
                                  : 'Redeem Reward'
                              }
                            >
                              <span>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={<RedeemIcon />}
                                  onClick={() => handleRedeem(reward)}
                                  disabled={reward.status !== 'Active' || redeeming || isRedeemDisabled(reward)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Redeem
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No rewards found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {!loading && (
          <TablePagination
            
            component="div"
            count={filteredRewards.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: 'primary.main' }} />
            Reward Details - {selectedReward?.rewardId}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedReward && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Reward ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedReward.rewardId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedReward.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Points Required
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StarsIcon sx={{ fontSize: 18, color: '#f57c00' }} />
                  {selectedReward.pointsRequired.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedReward.status}
                  size="small"
                  style={getStatusStyle(selectedReward.status)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Quantity Features
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedReward.hasQuantity ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <InventoryIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      {selectedReward.quantity}
                    </Box>
                  ) : (
                    <Chip label="Not Enabled" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#666' }} />
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Expiry Features
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedReward.hasExpiry ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StarsIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                      {selectedReward.expiryDays} days
                    </Box>
                  ) : (
                    <Chip label="Not Enabled" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#666' }} />
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedReward.description}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Success
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setSuccessDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Error
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => setErrorDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redeem Confirmation Dialog */}
      <Dialog open={redeemDialogOpen} onClose={handleCloseRedeemDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RedeemIcon sx={{ color: 'success.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Redeem Reward
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {redeemingReward && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to redeem this reward?
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reward Name
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                    {redeemingReward.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Points Required
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StarsIcon sx={{ fontSize: 18, color: '#f57c00' }} />
                    {redeemingReward.pointsRequired.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reward ID
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                    {redeemingReward.rewardId}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {redeemingReward.description}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseRedeemDialog}
            disabled={redeeming}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmRedeem}
            disabled={redeeming}
            startIcon={redeeming ? <CircularProgress size={18} color="inherit" /> : <RedeemIcon />}
            sx={{ textTransform: 'none' }}
          >
            {redeeming ? 'Redeeming...' : 'Confirm Redeem'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssociateReward;